import React, { useMemo } from 'react';
import {
  AlertTriangle,
  UserMinus,
  GitMerge,
  Building2,
  Shield,
  Zap,
  Users,
  Lock,
  Clock,
  TrendingDown
} from 'lucide-react';

interface ResistanceRisk {
  id: string;
  type: 'job_displacement' | 'political_complexity' | 'cross_department' | 'skill_gap' | 'process_ownership' | 'timeline_pressure' | 'resource_constraint';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  mitigation: string;
  affected_groups: string[];
}

interface ResistanceRiskFlagsProps {
  opportunity: {
    id: number;
    title: string;
    inefficiency_type?: string;
    affected_steps?: any[];
    implementation_complexity?: string;
    compliance_requirements?: any;
    integration_complexity_score?: number;
    governance_complexity_score?: number;
    resistance_risk_flags?: ResistanceRisk[];
  };
  compact?: boolean;
}

const ResistanceRiskFlags: React.FC<ResistanceRiskFlagsProps> = ({
  opportunity,
  compact = false
}) => {
  const risks = useMemo((): ResistanceRisk[] => {
    if (opportunity.resistance_risk_flags) {
      return opportunity.resistance_risk_flags;
    }

    const detectedRisks: ResistanceRisk[] = [];
    const affectedSteps = opportunity.affected_steps || [];
    const inefficiencyType = opportunity.inefficiency_type || '';
    const complexity = opportunity.implementation_complexity || 'medium';
    const integrationScore = opportunity.integration_complexity_score || 5;
    const governanceScore = opportunity.governance_complexity_score || 5;

    const isFullAutomation = inefficiencyType === 'manual_pattern' && affectedSteps.length >= 3;
    if (isFullAutomation) {
      const roles = new Set<string>();
      affectedSteps.forEach((step: any) => {
        if (step.owner) roles.add(step.owner);
        if (step.role) roles.add(step.role);
      });

      detectedRisks.push({
        id: 'job_displacement',
        type: 'job_displacement',
        severity: affectedSteps.length >= 5 ? 'critical' : 'high',
        title: 'Job Displacement Risk',
        description: `This automation may significantly impact ${roles.size || affectedSteps.length} role(s) whose primary responsibilities include these manual tasks.`,
        mitigation: 'Develop role transition plan, identify upskilling opportunities, and communicate early about new responsibilities.',
        affected_groups: Array.from(roles).slice(0, 5)
      });
    }

    const handoffCount = affectedSteps.filter((step: any) =>
      step.action_type === 'handoff' ||
      step.name?.toLowerCase().includes('handoff') ||
      step.name?.toLowerCase().includes('transfer')
    ).length;

    if (handoffCount >= 2 || inefficiencyType === 'handoff_delay') {
      const departments = new Set<string>();
      affectedSteps.forEach((step: any) => {
        if (step.department) departments.add(step.department);
      });

      detectedRisks.push({
        id: 'political_complexity',
        type: 'political_complexity',
        severity: handoffCount >= 4 ? 'high' : 'medium',
        title: 'Political Complexity',
        description: `Eliminating ${handoffCount} handoff(s) may affect established workflows and departmental boundaries.`,
        mitigation: 'Engage department heads early, establish clear ownership model, and document benefits for all parties.',
        affected_groups: Array.from(departments).slice(0, 5)
      });
    }

    const departments = new Set<string>();
    affectedSteps.forEach((step: any) => {
      if (step.department) departments.add(step.department);
    });

    if (departments.size >= 3) {
      detectedRisks.push({
        id: 'cross_department',
        type: 'cross_department',
        severity: departments.size >= 5 ? 'high' : 'medium',
        title: 'Cross-Department Coordination',
        description: `This change spans ${departments.size} departments, requiring extensive coordination and alignment.`,
        mitigation: 'Establish cross-functional steering committee, assign department champions, schedule regular alignment meetings.',
        affected_groups: Array.from(departments).slice(0, 5)
      });
    }

    if (complexity === 'high' || integrationScore >= 7) {
      detectedRisks.push({
        id: 'skill_gap',
        type: 'skill_gap',
        severity: integrationScore >= 8 ? 'high' : 'medium',
        title: 'Technical Skill Gap',
        description: 'Implementation requires technical capabilities that may not exist in current teams.',
        mitigation: 'Assess current team capabilities, plan for training or external support, consider phased knowledge transfer.',
        affected_groups: ['IT Team', 'Process Owners', 'End Users']
      });
    }

    const hasLongTenureProcess = affectedSteps.some((step: any) =>
      step.description?.toLowerCase().includes('legacy') ||
      step.description?.toLowerCase().includes('established') ||
      step.name?.toLowerCase().includes('traditional')
    );

    if (hasLongTenureProcess || governanceScore >= 7) {
      detectedRisks.push({
        id: 'process_ownership',
        type: 'process_ownership',
        severity: 'medium',
        title: 'Process Ownership Resistance',
        description: 'Changing established processes may face resistance from long-standing process owners.',
        mitigation: 'Involve process owners in design, acknowledge their expertise, position them as change champions.',
        affected_groups: ['Process Owners', 'Senior Staff']
      });
    }

    if (complexity === 'high' && affectedSteps.length >= 5) {
      detectedRisks.push({
        id: 'timeline_pressure',
        type: 'timeline_pressure',
        severity: 'medium',
        title: 'Timeline Pressure',
        description: 'Complex implementation with many affected areas may create unrealistic timeline expectations.',
        mitigation: 'Set realistic milestones, build in buffer time, communicate timeline constraints proactively.',
        affected_groups: ['Project Team', 'Stakeholders']
      });
    }

    return detectedRisks;
  }, [opportunity]);

  const getRiskIcon = (type: string) => {
    switch (type) {
      case 'job_displacement': return <UserMinus className="w-4 h-4" />;
      case 'political_complexity': return <GitMerge className="w-4 h-4" />;
      case 'cross_department': return <Building2 className="w-4 h-4" />;
      case 'skill_gap': return <Zap className="w-4 h-4" />;
      case 'process_ownership': return <Lock className="w-4 h-4" />;
      case 'timeline_pressure': return <Clock className="w-4 h-4" />;
      case 'resource_constraint': return <TrendingDown className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'from-red-50 to-orange-50 border-red-200';
      case 'high': return 'from-orange-50 to-amber-50 border-orange-200';
      case 'medium': return 'from-yellow-50 to-amber-50 border-yellow-200';
      default: return 'from-blue-50 to-indigo-50 border-blue-200';
    }
  };

  if (risks.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {risks.map(risk => (
          <div
            key={risk.id}
            className={`px-2 py-1 rounded-lg text-xs font-medium border flex items-center gap-1 ${getSeverityColor(risk.severity)}`}
            title={risk.description}
          >
            {getRiskIcon(risk.type)}
            <span>{risk.title}</span>
          </div>
        ))}
      </div>
    );
  }

  const criticalCount = risks.filter(r => r.severity === 'critical').length;
  const highCount = risks.filter(r => r.severity === 'high').length;

  return (
    <div className="border border-orange-200 rounded-lg bg-gradient-to-br from-orange-50 to-amber-50 p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-orange-900 flex items-center gap-2">
          <Shield className="w-5 h-5 text-orange-600" />
          Resistance Risk Assessment
        </h4>
        <div className="flex gap-2">
          {criticalCount > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded border border-red-300">
              {criticalCount} Critical
            </span>
          )}
          {highCount > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded border border-orange-300">
              {highCount} High
            </span>
          )}
          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded border border-gray-300">
            {risks.length} Total Risks
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {risks.map(risk => (
          <div
            key={risk.id}
            className={`bg-gradient-to-r ${getSeverityBgColor(risk.severity)} rounded-lg border p-4`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${getSeverityColor(risk.severity)}`}>
                {getRiskIcon(risk.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-gray-900">{risk.title}</span>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded ${getSeverityColor(risk.severity)}`}>
                    {risk.severity.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-2">{risk.description}</p>

                <div className="bg-white/60 rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    Mitigation Strategy
                  </div>
                  <p className="text-sm text-gray-800">{risk.mitigation}</p>
                </div>

                {risk.affected_groups.length > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <Users className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-600">Affected:</span>
                    <div className="flex flex-wrap gap-1">
                      {risk.affected_groups.map((group, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                          {group}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {(criticalCount > 0 || highCount > 0) && (
        <div className="mt-4 p-3 bg-amber-100 rounded-lg border border-amber-300">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Recommendation:</strong> Address critical and high-severity risks before proceeding with implementation.
              Consider stakeholder engagement sessions and develop detailed mitigation plans.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResistanceRiskFlags;
